import { useEffect, useState, useCallback } from "react";
import Layout from "@/components/Layout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useClientScope } from "@/hooks/useClientScope";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ScrollText, Loader2, Search, Download, Undo2 } from "lucide-react";
import { CategoryBadge, StatusBadge, LangBadge } from "@/components/CommentBits";
import { timeAgo } from "@/lib/time";

export default function RemovalLog() {
  const { user } = useAuth();
  const { ClientPicker, scopeParam, ready } = useClientScope();
  const [items, setItems] = useState(null);
  const [search, setSearch] = useState("");
  const canWrite = user?.role !== "client_viewer";

  const load = useCallback(() => {
    if (!ready) return;
    const parts = ["status=removed"];
    if (scopeParam) parts.push(scopeParam);
    if (search) parts.push(`search=${encodeURIComponent(search)}`);
    api.get(`/comments?${parts.join("&")}`).then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, [scopeParam, ready, search]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const undo = async (id) => {
    try {
      await api.post(`/comments/${id}/undo`);
      toast.success("Comment restored");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const exportCsv = async () => {
    try {
      const parts = ["status=removed"];
      if (scopeParam) parts.push(scopeParam);
      const { data } = await api.get(`/comments/export?${parts.join("&")}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "como_removal_log.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV exported");
    } catch (e) { toast.error("Export failed"); }
  };

  return (
    <Layout>
      <div className="animate-fade-in-up space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="text-sm text-slate-400 font-medium">Every removal, logged</p>
            <h1 className="font-heading text-3xl sm:text-4xl font-medium text-slate-900 tracking-tight">Removal Log</h1>
          </div>
          <div className="flex items-center gap-3">
            <ClientPicker />
            <button onClick={exportCsv} data-testid="export-csv" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold">
              <Download className="w-4 h-4" /> Export CSV
            </button>
          </div>
        </div>

        <div className="relative max-w-sm">
          <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search comment or author..." data-testid="log-search"
            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-slate-200 bg-card text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brass-500/40" />
        </div>

        {items === null ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-indigo-600 animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="bg-card rounded-2xl border border-slate-100 p-12 text-center">
            <ScrollText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">No removals yet.</p>
          </div>
        ) : (
          <div className="bg-card rounded-2xl border border-slate-100 shadow-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50/50">
                    {["Comment", "Author", "Account", "Category", "Removed", ""].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((c) => (
                    <tr key={c.id} data-testid="log-row" className="border-t border-slate-50 hover:bg-indigo-50/30 transition-colors">
                      <td className="px-4 py-3 min-w-[16rem] max-w-md">
                        <p className="text-sm text-slate-700 line-clamp-2 mb-1" title={c.text}>{c.text}</p>
                        <LangBadge language={c.language} />
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">{c.author_name}</td>
                      <td className="px-4 py-3 text-sm text-slate-500 max-w-[18rem] whitespace-nowrap">
                        <p className="truncate" title={c.account_name}>{c.account_name}</p>
                      </td>
                      <td className="px-4 py-3"><CategoryBadge category={c.ai?.category} /></td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <StatusBadge status={c.status} />
                        <p className="text-xs text-slate-400 mt-1" title={c.decided_at ? new Date(c.decided_at).toLocaleString() : ""}>
                          {c.decided_by === "system" || !c.decided_by ? "by system" : `by ${c.decided_by}`}
                          {c.decided_at ? ` · ${timeAgo(c.decided_at)}` : ""}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {canWrite && (
                          <button onClick={() => undo(c.id)} data-testid={`undo-${c.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-indigo-50 hover:text-indigo-600 text-slate-600 text-xs font-semibold">
                            <Undo2 className="w-3.5 h-3.5" /> Undo
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
